import { emit } from './client.js';
import { getCurrentTraceId } from './trace.js';
import { currentFlowId } from './track.js';
import { normalizeName } from './util.js';

/** Canonicalize a data-track label to the interaction allow-list, or null.
 *  stripDigits drops trailing id-ish runs (cardinality guard, e.g. "row_12345"). */
export function normalizeLabel(raw) {
  return normalizeName(raw, { stripDigits: true });
}

/** Emit an interaction event programmatically — for non-click interactions
 *  (keyboard nudge, drag-resize) that the delegated click listener can't
 *  capture. Mirrors the click path: same label normalization, active flow_id
 *  tagging, and trace id. Never throws (telemetry cardinal rule). */
export function trackInteraction(label, props = {}) {
  try {
    const name = normalizeLabel(label);
    if (!name) return false;
    const fid = currentFlowId();
    const merged = fid ? { ...props, flow_id: fid } : { ...props };
    return emit({ event_type: 'interaction', name, trace_id: getCurrentTraceId(), props: merged });
  } catch {
    return false;
  }
}

function nearestTracked(start) {
  let el = start;
  while (el && el.nodeType === 1) {
    if (el.hasAttribute('data-no-track')) return null;
    if (el.dataset && el.dataset.track != null) return el;
    el = el.parentElement;
  }
  return null;
}

function onClick(e) {
  try {
    const el = nearestTracked(e.target);
    if (!el) return;
    // data-track-value carries a bounded low-cardinality variant (authors keep it
    // small). Passed through as-is — no normalization; the byte cap in client.js is
    // the backstop. Delegated to trackInteraction so the click and programmatic
    // paths share one code path (#256 + telemetry round-2).
    const props = {};
    const tv = el.getAttribute('data-track-value');
    if (tv) props.value = tv;
    trackInteraction(el.dataset.track, props);
  } catch {
    /* telemetry must never break a user click */
  }
}

let attached = false;

/** Attach the single delegated click listener. Idempotent. */
export function registerInteractions() {
  if (attached) return;
  document.addEventListener('click', onClick, { capture: true, passive: true });
  attached = true;
}

/** Detach the click listener (HMR teardown / tests). */
export function stopInteractions() {
  document.removeEventListener('click', onClick, { capture: true });
  attached = false;
}

export function _resetForTest() { stopInteractions(); }
